/**
 * Amplitude active backfill pipeline wizard for /admin/conversion-roi.
 * Mirrors PostHog/Mixpanel wizards — API Key + Secret Key.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plug, CheckCircle2, AlertTriangle, RefreshCw, Trash2, ShieldCheck, ExternalLink, History } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ProbeBadge({ state }) {
  if (state === 'idle') return null;
  if (state === 'testing') return <Badge className="bg-slate-700/60 text-slate-200">Testing…</Badge>;
  if (state === 'ok') return <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Connected</Badge>;
  return <Badge className="bg-rose-500/20 text-rose-200 border border-rose-400/40"><AlertTriangle className="w-3 h-3 inline mr-1" /> Failed</Badge>;
}

export default function AmplitudePipelineWizard({ onBackfillComplete }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    region_host: 'https://amplitude.com',
    api_key: '',
    secret_key: '',
  });
  const [probe, setProbe] = useState({ state: 'idle', msg: null });
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/amplitude-pipeline/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setConfig(d.connected ? d : null);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const callApi = async (path, method, body) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const text = await r.text();
    let json = null;
    try { json = JSON.parse(text); } catch { /* non-json */ }
    return { ok: r.ok, status: r.status, body: json || text };
  };

  const test = async () => {
    setProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/amplitude-pipeline/test', 'POST', form);
    if (r.ok && r.body.ok) {
      setProbe({ state: 'ok', msg: r.body.note ? `Reachable (${r.body.note.replace(/_/g, ' ')})` : 'Reachable' });
    } else setProbe({ state: 'fail', msg: r.body?.error || r.body?.detail || `HTTP ${r.status}` });
  };

  const connect = async () => {
    setProbe({ state: 'testing', msg: null });
    const r = await callApi('/api/amplitude-pipeline/connect', 'POST', form);
    if (r.ok) {
      setProbe({ state: 'ok', msg: 'Connected' });
      setForm({ region_host: 'https://amplitude.com', api_key: '', secret_key: '' });
      refresh();
    } else setProbe({ state: 'fail', msg: r.body?.detail || `HTTP ${r.status}` });
  };

  const backfill = async (days) => {
    setBackfillBusy(true);
    setBackfillResult(null);
    const r = await callApi(`/api/amplitude-pipeline/backfill?days=${days}`, 'POST');
    setBackfillBusy(false);
    if (r.ok) {
      setBackfillResult(r.body);
      onBackfillComplete?.(r.body);
      refresh();
    } else setBackfillResult({ error: r.body?.detail || `HTTP ${r.status}` });
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Amplitude? Encrypted keys will be deleted. Already-imported conversion_events are kept.')) return;
    await callApi('/api/amplitude-pipeline/config', 'DELETE');
    refresh();
    setBackfillResult(null);
  };

  const canSubmit = form.api_key && form.secret_key;

  return (
    <Card className="bg-slate-950 border-emerald-500/30" data-testid="amplitude-pipeline-wizard">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Plug className="w-4 h-4 text-emerald-400" />
          Amplitude active pipeline — auto-import conversion events
          <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-[9px] font-mono tracking-widest ml-1">P1</Badge>
        </CardTitle>
        <p className="text-sm text-slate-200 mt-1">Project-scoped API Key + Secret Key pulls events from the Amplitude Export API (zipped NDJSON). Same encrypted-vault as PostHog/Mixpanel/cloud creds.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {config ? (
          <div className="rounded-md bg-slate-900 border border-emerald-500/30 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <ShieldCheck className="w-4 h-4 text-emerald-300" />
                  <span className="text-white font-bold">Connected</span>
                  <code className="text-[10px] text-slate-300 font-mono">{config.region_host}</code>
                </div>
                {config.last_backfill_at && (
                  <div className="text-xs text-slate-300 mt-0.5">
                    Last backfill: {new Date(config.last_backfill_at).toLocaleString()} · {config.last_backfill_count} events
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => backfill(7)} disabled={backfillBusy} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs" data-testid="amplitude-backfill-7d-btn">
                  {backfillBusy ? 'Syncing…' : <><RefreshCw className="w-3 h-3 mr-1" /> Backfill 7d</>}
                </Button>
                <Button size="sm" onClick={() => backfill(30)} disabled={backfillBusy} className="bg-emerald-700 hover:bg-emerald-600 text-white h-8 text-xs" data-testid="amplitude-backfill-30d-btn">
                  <History className="w-3 h-3 mr-1" /> 30d
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnect} className="text-rose-300 hover:text-rose-200 h-8 px-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            {backfillResult && (
              <div className="mt-3 text-xs">
                {backfillResult.error ? (
                  <div className="text-rose-300">{backfillResult.error}</div>
                ) : (
                  <div className="text-emerald-300">
                    ✓ {backfillResult.events_fetched} fetched · {backfillResult.events_upserted} imported · {backfillResult.events_skipped} skipped · window {backfillResult.window_days}d
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-md bg-slate-900 border border-emerald-500/20 p-3" data-testid="amplitude-form">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold text-white">Amplitude project credentials</div>
              <ProbeBadge state={probe.state} />
            </div>
            <select
              value={form.region_host}
              onChange={(e) => setForm({ ...form, region_host: e.target.value })}
              className="w-full mb-2 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-white"
              data-testid="amplitude-host-select"
            >
              <option value="https://amplitude.com">US Cloud (amplitude.com)</option>
              <option value="https://analytics.eu.amplitude.com">EU Cloud (analytics.eu.amplitude.com)</option>
            </select>
            <input
              type="text"
              placeholder="API Key (Project settings → API Keys)"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              className="w-full mb-2 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-white"
              data-testid="amplitude-api-key-input"
            />
            <input
              type="password"
              placeholder="Secret Key"
              value={form.secret_key}
              onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
              className="w-full mb-2 bg-slate-950 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-white"
              data-testid="amplitude-secret-key-input"
            />
            <div className="flex gap-2 mt-1 flex-wrap">
              <Button size="sm" variant="outline" onClick={test} disabled={!canSubmit} className="text-xs h-7 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="amplitude-test-btn">Test</Button>
              <Button size="sm" onClick={connect} disabled={!canSubmit} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-7" data-testid="amplitude-connect-btn">Save & connect</Button>
              <a href="https://amplitude.com/docs/apis/analytics/export" target="_blank" rel="noreferrer" className="ml-auto text-xs text-slate-200 hover:text-emerald-300 flex items-center gap-1">
                Get keys from Project settings <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {probe.msg && <div className={`mt-2 text-xs ${probe.state === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>{probe.msg}</div>}
            <div className="text-[10px] text-slate-300 italic mt-2">
              We track: <code className="text-emerald-300">Sign Up</code>, <code className="text-emerald-300">Purchase</code>, <code className="text-emerald-300">Subscription Started</code>, <code className="text-emerald-300">Upgrade</code>, <code className="text-emerald-300">Churn</code> (and common synonyms).
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
